
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { saveUserSetting, getUserLocation } from '@/utils/userSettingsUtils';
import { useAuth } from '@/contexts/AuthContext';
import CityAutocomplete from './CityAutocomplete';
import { findCityByName, DEFAULT_LOCATION } from '@/data/israelCities';

const LocationSettings = () => {
  const [location, setLocation] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  // Load existing location on component mount
  useEffect(() => {
    const loadExistingLocation = async () => {
      if (!user?.id) return;
      
      try {
        const userLocation = await getUserLocation(user.id);
        if (userLocation.lat && userLocation.lng) {
          setLatitude(userLocation.lat);
          setLongitude(userLocation.lng);
          
          if (userLocation.name) {
            setLocation(userLocation.name);
            
            // Try to find the city in our predefined list
            const city = findCityByName(userLocation.name);
            if (city) {
              setLocationId(city.id);
            }
          }
          
          // Check if we have a stored locationId
          if (userLocation.locationId) {
            setLocationId(userLocation.locationId);
          }
        }
      } catch (error) {
        console.error('Error loading existing location:', error);
      }
    };

    loadExistingLocation();
  }, [user?.id]);

  const handleLocationSelect = async (selectedLocation: { name: string; lat: string; lng: string; locationId: number }) => {
    setLatitude(selectedLocation.lat);
    setLongitude(selectedLocation.lng);
    setLocation(selectedLocation.name);
    setLocationId(selectedLocation.locationId);
    
    toast({
      title: "מיקום נבחר",
      description: `נבחר ${selectedLocation.name} (ID: ${selectedLocation.locationId})`,
    });
  };

  const handleSave = async () => {
    if (!latitude || !longitude || !locationId) {
      toast({
        title: "שגיאה",
        description: "נא לבחור מיקום תקין מהרשימה",
        variant: "destructive"
      });
      return;
    }
    
    if (!user?.id) {
      toast({
        title: "שגיאה בזיהוי משתמש",
        description: "לא ניתן לשמור מיקום ללא משתמש מחובר.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      console.log('Saving location for user:', user.id, latitude, longitude, locationId);
      
      // Save location data to user_settings
      await saveUserSetting('location_lat', latitude, user.id);
      await saveUserSetting('location_lng', longitude, user.id);
      await saveUserSetting('location_name', location, user.id);
      await saveUserSetting('location_id', locationId, user.id);
      
      // Also save complete location object
      const locationData = {
        name: location,
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
        locationId: locationId,
        tzid: 'Asia/Jerusalem'
      };
      await saveUserSetting('location', locationData, user.id);
      
      toast({
        title: "מיקום נשמר",
        description: "הגדרות המיקום נשמרו בהצלחה",
      });
    } catch (error) {
      console.error('Error saving location:', error);
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בשמירת המיקום",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: "לא נתמך",
        description: "דפדפן זה אינו תומך באיתור מיקום",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude.toString();
        const lng = position.coords.longitude.toString();
        
        setLatitude(lat);
        setLongitude(lng);
        
        // Use default location (Jerusalem) for detected coordinates
        setLocation(DEFAULT_LOCATION.name);
        setLocationId(DEFAULT_LOCATION.id);
        
        setIsLoading(false);
        
        toast({
          title: "מיקום אותר",
          description: `המיקום הנוכחי שלך אותר. נבחרה ירושלים כברירת מחדל.`,
        });
      },
      (error) => {
        setIsLoading(false);
        
        let message;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = "הגישה למיקום נדחתה";
            break;
          case error.POSITION_UNAVAILABLE:
            message = "מידע המיקום אינו זמין";
            break;
          case error.TIMEOUT:
            message = "פג הזמן הקצוב לבקשת המיקום";
            break;
          default:
            message = "אירעה שגיאה לא ידועה";
        }
        
        toast({
          title: "שגיאה",
          description: message,
          variant: "destructive"
        });
      },
      { timeout: 10000 }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>הגדרות מיקום</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="location">בחר עיר</Label>
          <CityAutocomplete
            value={location}
            onChange={setLocation}
            onLocationSelect={handleLocationSelect}
            placeholder="הקלד שם עיר (למשל: תל אביב, ירושלים)"
          />
          {locationId && (
            <p className="text-sm text-gray-600">
              מזהה מיקום: {locationId}
            </p>
          )}
        </div>

        <Button 
          variant="outline" 
          onClick={detectCurrentLocation} 
          className="w-full"
          disabled={isLoading}
        >
          <MapPin className="h-4 w-4 mr-2" />
          איתור המיקום הנוכחי שלי
        </Button>

        <Button 
          onClick={handleSave} 
          className="w-full"
          disabled={isLoading || !latitude || !longitude || !locationId}
        >
          שמור הגדרות מיקום
        </Button>
      </CardContent>
    </Card>
  );
};

export default LocationSettings;
